import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {RecordAuditResponseBody, useUpdateRecordAuditHeaderMutation} from '@dms/api/core';
import {RecordIdHttpModel} from '@dms/api/shared';

import {ApiError, parseDate} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {RecordAuditMetadataFormValue} from './recordAuditMetadataFormSchema';
import {toUpdateRecordAuditHeaderBody} from './toUpdateRecordAuditHeaderBody';

type EmployeeRef = {employeeId: string};

// Order-independent set comparison so a reordered participant list still reconciles to the server.
const isSameEmployeeSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const isSameRecord = (a: RecordIdHttpModel | null, b: RecordIdHttpModel | null) =>
  (a?.id ?? null) === (b?.id ?? null) && (a?.resourceId ?? null) === (b?.resourceId ?? null);

// Display names come straight from the audit response (auditor, responsible person, participants),
// so the team panel doesn't have to fetch the whole user list.
const buildNameByEmployeeId = (audit: RecordAuditResponseBody) =>
  new Map(
    [
      ...(isNotNil(audit.auditor) ? [audit.auditor] : []),
      ...(isNotNil(audit.responsiblePerson) ? [audit.responsiblePerson] : []),
      ...audit.participants.map((participant) => participant.employee),
    ].map((employee) => [employee.id, `${employee.firstName} ${employee.lastName}`.trim()])
  );

/**
 * Optimistic state machine for the record-audit header (auditor, responsible person, participants,
 * branch, paired record).
 *
 * Each override is the value the user just picked/removed; it is shown instantly and kept until the
 * refetched audit catches up to it, after which the server value takes back over (see the
 * `effective*` derivations). An override applies only while it diverges from the server, so a stale
 * override never masks a later server change. On a failed persist, only the field that changed is
 * reverted — a failed edit to one field never wipes another field's in-flight optimistic state.
 *
 * This is the single source of truth for what is shown, what the add-participant dialog excludes,
 * and what every persist call sends — so remove-then-(re)add sequences can't resurrect a removed
 * person or drop a freshly added one.
 */
export function useOptimisticAuditHeader(audit: RecordAuditResponseBody) {
  const [updateHeader] = useUpdateRecordAuditHeaderMutation();

  // `undefined`/`null` = no override. For the record, `null` = optimistically unpaired.
  const [auditorOverride, setAuditorOverride] = useState<string | undefined>(undefined);
  const [responsibleOverride, setResponsibleOverride] = useState<
    {value: string | null} | undefined
  >(undefined);
  const [optimisticParticipants, setOptimisticParticipants] = useState<EmployeeRef[] | undefined>(
    undefined
  );
  const [branchOverride, setBranchOverride] = useState<string | undefined>(undefined);
  const [recordOverride, setRecordOverride] = useState<RecordIdHttpModel | null | undefined>(
    undefined
  );

  const serverAuditorId = audit.auditor?.id ?? null;
  const serverResponsiblePersonId = audit.responsiblePerson?.id ?? null;
  const serverBranchId = audit.branch.id;
  const serverRecord = audit.recordId;
  const serverParticipants: EmployeeRef[] = audit.participants.map((participant) => ({
    employeeId: participant.employee.id,
  }));

  const nameByEmployeeId = buildNameByEmployeeId(audit);

  const effectiveAuditorId =
    isNotNil(auditorOverride) && auditorOverride !== serverAuditorId
      ? auditorOverride
      : serverAuditorId;
  const effectiveResponsiblePersonId =
    isNotNil(responsibleOverride) && responsibleOverride.value !== serverResponsiblePersonId
      ? responsibleOverride.value
      : serverResponsiblePersonId;
  const effectiveBranchId =
    isNotNil(branchOverride) && branchOverride !== serverBranchId ? branchOverride : serverBranchId;
  const effectiveRecord =
    recordOverride !== undefined && !isSameRecord(recordOverride, serverRecord)
      ? recordOverride
      : serverRecord;
  const effectiveParticipants =
    isNotNil(optimisticParticipants) &&
    !isSameEmployeeSet(
      optimisticParticipants.map((participant) => participant.employeeId),
      serverParticipants.map((participant) => participant.employeeId)
    )
      ? optimisticParticipants
      : serverParticipants;

  // Skeleton flags: branch/record reconcile by value convergence with the refetched audit; a person
  // slot whose effective id has no resolved name yet (a just-picked person the refetch hasn't
  // returned) shows a loading skeleton.
  const isBranchUpdating = effectiveBranchId !== serverBranchId;
  const isRecordUpdating =
    recordOverride !== undefined && !isSameRecord(recordOverride, serverRecord);
  const isAuditorUpdating =
    isNotNil(effectiveAuditorId) && !nameByEmployeeId.has(effectiveAuditorId);
  const isResponsiblePersonUpdating =
    isNotNil(effectiveResponsiblePersonId) && !nameByEmployeeId.has(effectiveResponsiblePersonId);

  const auditorName = isNotNil(effectiveAuditorId)
    ? (nameByEmployeeId.get(effectiveAuditorId) ?? null)
    : null;
  const responsiblePersonName = isNotNil(effectiveResponsiblePersonId)
    ? (nameByEmployeeId.get(effectiveResponsiblePersonId) ?? null)
    : null;

  // Participants split into resolved (named) cards and not-yet-named loading cards.
  const realParticipants = effectiveParticipants.filter((participant) =>
    nameByEmployeeId.has(participant.employeeId)
  );
  const loadingParticipantCount = effectiveParticipants.length - realParticipants.length;

  // Persist a header change immediately, reusing the current effective values for everything not
  // overridden. On failure, `revert` resets only the field that changed (the field whose override
  // this call set) so an unrelated in-flight optimistic edit is never clobbered.
  const persist = (
    overrides: {
      auditorEmployeeId?: string;
      responsiblePersonEmployeeId?: string | null;
      branchId?: string;
      participants?: RecordAuditMetadataFormValue['participants'];
      pairedRecord?: RecordIdHttpModel | null;
    },
    revert: () => void
  ) => {
    const values: RecordAuditMetadataFormValue = {
      auditorEmployeeId: overrides.auditorEmployeeId ?? effectiveAuditorId,
      responsiblePersonEmployeeId:
        overrides.responsiblePersonEmployeeId !== undefined
          ? overrides.responsiblePersonEmployeeId
          : effectiveResponsiblePersonId,
      date: parseDate(audit.date),
      branchId: overrides.branchId ?? effectiveBranchId,
      participants: overrides.participants ?? effectiveParticipants,
    };

    updateHeader({
      auditId: audit.id,
      body: toUpdateRecordAuditHeaderBody({
        values,
        pairedRecord:
          overrides.pairedRecord !== undefined ? overrides.pairedRecord : effectiveRecord,
      }),
    })
      .unwrap()
      // No success toast: the optimistic override already reflects the change in the header.
      .catch((error: ApiError) => {
        revert();
        handleApiError(error);
      });
  };

  const selectAuditor = (auditorEmployeeId: string) => {
    setAuditorOverride(auditorEmployeeId);
    persist({auditorEmployeeId}, () => setAuditorOverride(undefined));
  };

  const selectResponsiblePerson = (responsiblePersonEmployeeId: string) => {
    setResponsibleOverride({value: responsiblePersonEmployeeId});
    persist({responsiblePersonEmployeeId}, () => setResponsibleOverride(undefined));
  };

  const clearResponsiblePerson = () => {
    setResponsibleOverride({value: null});
    persist({responsiblePersonEmployeeId: null}, () => setResponsibleOverride(undefined));
  };

  const selectBranch = (branchId: string) => {
    setBranchOverride(branchId);
    persist({branchId}, () => setBranchOverride(undefined));
  };

  const pairRecord = (record: RecordIdHttpModel) => {
    setRecordOverride(record);
    persist({pairedRecord: record}, () => setRecordOverride(undefined));
  };

  const clearPair = () => {
    setRecordOverride(null);
    persist({pairedRecord: null}, () => setRecordOverride(undefined));
  };

  const addParticipants = (participants: EmployeeRef[]) => {
    const next = [...effectiveParticipants, ...participants];
    setOptimisticParticipants(next);
    persist({participants: next}, () => setOptimisticParticipants(undefined));
  };

  const removeParticipant = (index: number) => {
    const removedEmployeeId = realParticipants[index]?.employeeId;
    if (isNil(removedEmployeeId)) {
      return;
    }
    const next = effectiveParticipants.filter(
      (participant) => participant.employeeId !== removedEmployeeId
    );
    setOptimisticParticipants(next);
    persist({participants: next}, () => setOptimisticParticipants(undefined));
  };

  return {
    effectiveAuditorId,
    effectiveResponsiblePersonId,
    auditorName,
    responsiblePersonName,
    effectiveBranchId,
    effectiveRecord,
    effectiveParticipants,
    realParticipants,
    loadingParticipantCount,
    nameByEmployeeId,
    isAuditorUpdating,
    isResponsiblePersonUpdating,
    isBranchUpdating,
    isRecordUpdating,
    selectAuditor,
    selectResponsiblePerson,
    clearResponsiblePerson,
    selectBranch,
    pairRecord,
    clearPair,
    addParticipants,
    removeParticipant,
  };
}
